/**
 * Этап 4 / KS-07 — the pure part of the rich-text editor.
 *
 * Everything here is free of React and of TipTap so it can be tested directly: the
 * two value conversions below are where a rich-text editor silently corrupts data,
 * and they are far easier to pin down as functions than through a mounted
 * ProseMirror instance.
 */
import { normalizeRichText, renderFreeMarkdown, isRichTextEmpty } from '@eer/shared';

/**
 * What to load into the editor for a stored value.
 *
 * Values written before the HTML transition are Markdown. Handing `**bold**` to the
 * editor as HTML would display it as literal asterisks, and the moment the user
 * saved, those asterisks would be frozen into the HTML as text — the markup would be
 * lost for good. Rendering on load prevents that. Nothing is written back until the
 * user saves, so this converts a VIEW, not the stored row.
 */
export function editorInitialContent(value: unknown): string {
  return normalizeRichText(value, renderFreeMarkdown);
}

/**
 * What to persist for what the editor currently holds.
 *
 * ProseMirror never has "no document": an emptied editor serializes to `<p></p>`.
 * Storing that would make every downstream emptiness check (a Send button gated on a
 * non-blank body, "no signature → skip") see content where the user sees none. So a
 * visually-empty document is normalized back to the empty string.
 *
 * An image-only document is NOT empty — a signature is sometimes just a logo
 * (`isRichTextEmpty` encodes that rule; it is shared with the server gate).
 */
export function normalizeEditorOutput(html: unknown): string {
  const value = String(html ?? '');
  return isRichTextEmpty(value) ? '' : value;
}

/**
 * True when a fresh editor value is materially the same as the stored one.
 *
 * Guards two independent nuisances: an `onChange` fired for a no-op keystroke would
 * mark a clean form dirty (and trip the unsaved-changes guard), and pushing a value
 * back into the editor that it just produced resets the caret to the start of the
 * document mid-typing.
 */
export function isSameRichText(a: unknown, b: unknown): boolean {
  const left = normalizeEditorOutput(a);
  const right = normalizeEditorOutput(b);
  if (left === right) return true;
  // `<p></p>` vs `''` vs `<p><br></p>` are all "empty" and must compare equal.
  return isRichTextEmpty(left) && isRichTextEmpty(right);
}

/**
 * Text colours offered by the toolbar — 16 swatches, shown as coloured circles.
 *
 * A closed palette rather than a native colour picker: these values must survive the
 * shared sanitizer's colour pattern AND render in Outlook, and an arbitrary
 * `rgba()`/`hsl()` from a picker satisfies neither. Hex only, for the same reason.
 *
 * Client feedback 2026-07-31: the first version was a `<select>` whose options were
 * colour NAMES. Picking a colour from a list of words means reading instead of
 * seeing — and the names were in Russian, which the interface must never contain.
 * Sixteen swatches, chosen to stay legible on white: four neutrals, then warm →
 * cool, so a recruiter finds "a red" without hunting.
 *
 * `label` is the `title`/`aria-label` of the swatch — English, like all UI text.
 */
export const RICH_TEXT_COLORS: readonly { readonly value: string; readonly label: string }[] = [
  { value: '#000000', label: 'Black' },
  { value: '#434343', label: 'Dark gray' },
  { value: '#757575', label: 'Gray' },
  { value: '#b7b7b7', label: 'Light gray' },
  { value: '#7f1d1d', label: 'Dark red' },
  { value: '#dc2626', label: 'Red' },
  { value: '#ea580c', label: 'Orange' },
  { value: '#d97706', label: 'Amber' },
  { value: '#ca8a04', label: 'Yellow' },
  { value: '#65a30d', label: 'Lime' },
  { value: '#16a34a', label: 'Green' },
  { value: '#047857', label: 'Dark green' },
  { value: '#0891b2', label: 'Teal' },
  { value: '#2563eb', label: 'Blue' },
  { value: '#1e3a8a', label: 'Navy' },
  { value: '#7c3aed', label: 'Purple' },
];

/** Label of the control that REMOVES the colour (kept out of the 16 swatches). */
export const RICH_TEXT_COLOR_CLEAR_LABEL = 'Default colour';

/** A toolbar button's identity — the component maps `id` to a TipTap command. */
export type RichTextCommandId =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strike'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'bulletList'
  | 'orderedList'
  | 'blockquote'
  | 'link'
  | 'unlink'
  | 'clear';

export interface RichTextToolbarItem {
  readonly id: RichTextCommandId;
  /** Glyph shown on the button (kept to text so no icon set is required). */
  readonly glyph: string;
  /** `title` + `aria-label`. English: the interface carries no Russian text. */
  readonly label: string;
}

/**
 * Toolbar layout, as data. Groups are rendered with a separator between them; the
 * order mirrors the markdown toolbar it replaces (B/I/U first) so the control does
 * not move under the recruiter's hand.
 */
export const RICH_TEXT_TOOLBAR: readonly (readonly RichTextToolbarItem[])[] = [
  [
    { id: 'bold', glyph: 'B', label: 'Bold' },
    { id: 'italic', glyph: 'I', label: 'Italic' },
    { id: 'underline', glyph: 'U', label: 'Underline' },
    { id: 'strike', glyph: 'S', label: 'Strikethrough' },
  ],
  [
    { id: 'h1', glyph: 'H1', label: 'Heading 1' },
    { id: 'h2', glyph: 'H2', label: 'Heading 2' },
    { id: 'h3', glyph: 'H3', label: 'Heading 3' },
  ],
  [
    { id: 'bulletList', glyph: '•', label: 'Bullet list' },
    { id: 'orderedList', glyph: '1.', label: 'Numbered list' },
    { id: 'blockquote', glyph: '❝', label: 'Quote' },
  ],
  [
    { id: 'link', glyph: '🔗', label: 'Link' },
    { id: 'unlink', glyph: '⛓', label: 'Remove link' },
    { id: 'clear', glyph: '✕', label: 'Clear formatting' },
  ],
];

/**
 * Validate a URL typed into the link prompt.
 *
 * The shared sanitizer would drop a bad scheme anyway, but silently: the user would
 * see their link vanish with no explanation. Rejecting here lets the UI say why.
 * A bare `example.com` is upgraded to `https://` — that is what the typist meant,
 * and a schemeless href would resolve against our own app.
 */
export function normalizeLinkHref(raw: string): string | null {
  const value = raw.trim();
  if (!value) return null;
  if (/^(?:https?:|mailto:)/i.test(value)) return value;
  // Anything with a different explicit scheme is refused, not rewritten —
  // `javascript:` must never become `https://javascript:`.
  if (/^[a-z][a-z0-9+.-]*:/i.test(value)) return null;
  return `https://${value}`;
}
